// eslint-disable-next-line max-classes-per-file
import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import styled from 'styled-components';
import _ from 'lodash';
import Select from 'react-select';
import { FixedSizeList } from 'react-window';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';

// We try to support all props as the FormSelect control, so this control
// would be a drop-in replacement for FormSelect for large data set.
const propTypes = {
  form: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  extra: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  help: PropTypes.string,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,
  fontSize: PropTypes.string,
  options: PropTypes.array.isRequired,
  showIdAfterName: PropTypes.bool,
  isRequired: PropTypes.bool,
  validator: PropTypes.func,
  onSelect: PropTypes.func,
};

const defaultProps = {
  initialValue: undefined,
  placeholder: '',
  disabled: false,
  allowClear: true,
  fontSize: '14px',
  showIdAfterName: false,
  isRequired: false,
};

// Style the react-select control to make it look as similar to the antd Select as possible.
const StyledFormItem = styled(styledComp.AntdFormItem)`
  .ant-form-item-control {
    height: 40px;
    padding: 4px 0;
    line-height: 30px;
    font-size: ${props => props.fontSize};

    &.has-error {
       div[class$=-control] {
         border: 1px solid #f5222d !important;
       }
    }

    div[class$=-control] {
      border: 1px solid #d9d9d9;
      cursor: pointer;

      &:hover {
        border: 1px solid #2e8de6;
      }

      &>div:first-child {
        padding: 0 12px;
      }

      div[class$=-IndicatorsContainer] {
        min-width: 26px;

        div[class$=-indicatorContainer] {
          color: rgba(0, 0, 0, 0.7);

          svg {
            width: 16px;
            height: 16px;
          }
        }

        span[class$=-indicatorSeparator] {
          background-color: transparent;
        }
      }
    }

    div[class$=-option][id^=react-select-] {
      padding: 0 12px;
      white-space: nowrap;
    }

    div[class$=-placeholder] {
      color: rgba(0, 0, 0, 0.34);
    }
  }
`;

/* eslint-disable */
class MenuList extends React.Component {
  render() {
    const ITEM_HEIGHT = 32;
    const { options, maxHeight, getValue, children } = this.props;
    const [value] = getValue();
    const initialOffset = Math.max(options.indexOf(value) * ITEM_HEIGHT, 0);

    return (
      <FixedSizeList
        height={maxHeight}
        itemCount={children.length}
        itemSize={ITEM_HEIGHT}
        initialScrollOffset={initialOffset}
      >
        {({ index, style }) => {
          return (<div style={{
            ...style,
            lineHeight: 2.5,
          }}>{children[index]}</div>);
        }}
      </FixedSizeList>
    );
  }
}
/* eslint-enable */

/**
 * This class is a drop-in replacement for FormSelect. Instead of basing on the antd
 * Select, it bases itself on react-select + react-window in order to boost performance
 * for large dataset (> 500 rows). We should only use it for large dataset because its
 * UI is still a bit unfit to the antd theme, compared to the FormSelect control.
 */
@autobind
class FormSelect2 extends BaseComponent {
  constructor(props) {
    super(props);
  }

  componentDidUpdate(prevProps) {
    const { form, name, options } = this.props;

    // If the options changed, we may need to clear the current selection.
    if (!_.isEqual(options, prevProps.options)) {
      const currentOpt = form.getFieldValue(name);
      if (currentOpt === null || currentOpt === undefined ||
        !options || !options.map(this.createKey).includes(currentOpt.value)
      ) {
        form.setFieldsValue({
          [name]: null,
        });
      }
    }
  }

  createKey(option) {
    return _.isstring(option) ? option : (option.value || option.id);
  }

  createText(option) {
    if (_.isstring(option)) {
      return option;
    } else if (option.text) {
      return option.text;
    } else if (option.name) { // name-id pair.
      return option.name + (this.props.showIdAfterName ? ` (${option.id})` : '');
    } else {
      return '';
    }
  }

  onChange(opt) {
    if (this.props.onSelect) {
      this.props.onSelect(opt ? opt.value : undefined);
    }
  }

  render() {
    const {
      form,
      name,
      label,
      extra,
      initialValue,
      placeholder,
      help,
      disabled,
      allowClear,
      options,
      isRequired,
      validator,
      // onSelect,
      ...layoutProps
    } = this.props;

    const { getFieldDecorator } = form;

    const requiredRule = { required: isRequired, message: 'This field should not be empty.' };
    const rules = [requiredRule, validator].filter(Boolean);

    const optionsInSelectFormat = options.map((option, index) => (
      { value: this.createKey(option), label: this.createText(option) }
    ));

    const defaultValue = (initialValue === null || initialValue === undefined) ? null : (
      optionsInSelectFormat.find(opt => opt.value === initialValue)
    );

    return (
      <StyledFormItem
        {...layoutProps}
        label={label}
        extra={extra}
        help={help}
      >
        {getFieldDecorator(name, {
          rules,
          initialValue: defaultValue,
        })(
          <Select
            placeholder={placeholder}
            theme={{
              spacing: { baseUnit: 0 }, // We will style ourselves.
            }}
            isDisabled={disabled}
            isClearable={allowClear}
            isSearchable
            options={optionsInSelectFormat}
            // defaultValue={defaultValue} // Use getFieldDecorator's initialValue instead.
            components={{ MenuList }}
            onChange={this.onChange}
          />,
        )}
      </StyledFormItem>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
FormSelect2.propTypes = propTypes;
FormSelect2.defaultProps = defaultProps;

export default FormSelect2;
